import React, { useRef, useEffect, useMemo } from 'react';
import { ButtonDropdown, Flashbar } from '@amzn/awsui-components-core';
import { createPortal } from 'react-dom';
import { ResolvedMatch, AttendeePriority } from '../../interfaces/interface';
import { MeetingService } from '../../services/MeetingService';
import { copyTextWithFeedback, ClipboardError, ClipboardResult } from '../../utils/clipboardUtils';
import { useFlashMessages } from '../../hooks/useFlashMessages';
import './CopyDropdown.scss';

interface CopyDropdownProps {
  resolvedAttendees: ResolvedMatch[];
  onMultiBadgerClick?: () => void;
  onDownloadCsv?: () => void;
}

const ALL_PRIORITIES = [AttendeePriority.Critical, AttendeePriority.Required, AttendeePriority.Optional] as const;
const CRITICAL_REQUIRED = [AttendeePriority.Critical, AttendeePriority.Required] as const;

export const CopyDropdown: React.FC<CopyDropdownProps> = ({ resolvedAttendees, onMultiBadgerClick, onDownloadCsv }) => {
  const { flashMessages, addFlashMessage, removeFlashMessage, announceMessage } = useFlashMessages({});
  const buttonRef = useRef<HTMLDivElement>(null);
  const [flashPosition, setFlashPosition] = React.useState({ top: 0, left: 0 });

  useEffect(() => {
    if (flashMessages.length > 0 && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setFlashPosition({
        top: rect.bottom + window.scrollY + 8,
        left: Math.max(0, rect.left + window.scrollX - 150)
      });
    }
  }, [flashMessages.length]);

  const handleCopyAttendees = async (label: string, priorities: ReadonlyArray<AttendeePriority>) => {
    const { formatted, count } = MeetingService.formatAttendeesForCopyByPriorities(resolvedAttendees, priorities);
    if (!formatted) {
      addFlashMessage('error', `No ${label ? label + ' ' : ''}attendees with email addresses found to copy.`);
      return;
    }
    await copyTextWithFeedback(
      formatted,
      (_result: ClipboardResult) => {
        addFlashMessage('success', `Copied ${count} ${label ? label + ' ' : ''}attendee${count !== 1 ? 's' : ''} to clipboard for Outlook.`);
      },
      (_error: ClipboardError) => {
        addFlashMessage('error', 'Failed to copy to clipboard. Please try again.');
      }
    );
  };

  const handleCopyExecutiveAssistantsForOutlook = async () => {
    const formattedText = MeetingService.formatExecutiveAssistantsForCopy(resolvedAttendees);
    if (!formattedText) {
      addFlashMessage('error', 'No valid executive assistants with email addresses found to copy.');
      return;
    }
    await copyTextWithFeedback(
      formattedText,
      (_result: ClipboardResult) => {
        const eas = MeetingService.collectExecutiveAssistants(resolvedAttendees);
        addFlashMessage('success', `Copied ${eas.length} executive assistant${eas.length !== 1 ? 's' : ''} to clipboard for Outlook.`);
      },
      (_error: ClipboardError) => {
        addFlashMessage('error', 'Failed to copy to clipboard. Please try again.');
      }
    );
  };

  const { allCount, criticalCount, requiredCount, criticalRequiredCount, optionalCount } = useMemo(
    () => ({
      allCount: MeetingService.countAttendeesWithEmailByPriorities(resolvedAttendees, ALL_PRIORITIES),
      criticalCount: MeetingService.countAttendeesWithEmailByPriorities(resolvedAttendees, [AttendeePriority.Critical]),
      requiredCount: MeetingService.countAttendeesWithEmailByPriorities(resolvedAttendees, [AttendeePriority.Required]),
      criticalRequiredCount: MeetingService.countAttendeesWithEmailByPriorities(resolvedAttendees, CRITICAL_REQUIRED),
      optionalCount: MeetingService.countAttendeesWithEmailByPriorities(resolvedAttendees, [AttendeePriority.Optional])
    }),
    [resolvedAttendees]
  );

  const eaCount = useMemo(() => MeetingService.collectExecutiveAssistants(resolvedAttendees).length, [resolvedAttendees]);

  const dropdownItems = [
    {
      id: 'view-badger',
      text: 'View Hierarchy in Multi-Badger',
      iconName: 'external' as const,
      external: true,
      externalIconAriaLabel: '(opens Attendee Hierarchy in Badger in new tab)',
      disabled: !onMultiBadgerClick
    },
    {
      id: 'download-csv',
      text: 'Download as CSV',
      iconName: 'download' as const,
      disabled: !onDownloadCsv
    },
    {
      id: 'copy-attendee-all',
      text: `Copy All Attendee Email Addresses (${allCount})`,
      iconName: 'copy' as const,
      disabled: allCount === 0
    },
    {
      id: 'copy-attendee-critical',
      text: `Copy Critical Email Addresses (${criticalCount})`,
      iconName: 'copy' as const,
      disabled: criticalCount === 0
    },
    {
      id: 'copy-attendee-required',
      text: `Copy Required Email Addresses (${requiredCount})`,
      iconName: 'copy' as const,
      disabled: requiredCount === 0
    },
    {
      id: 'copy-attendee-critical-required',
      text: `Copy Critical & Required Email Addresses (${criticalRequiredCount})`,
      iconName: 'copy' as const,
      disabled: criticalRequiredCount === 0
    },
    {
      id: 'copy-attendee-optional',
      text: `Copy Optional Email Addresses (${optionalCount})`,
      iconName: 'copy' as const,
      disabled: optionalCount === 0
    },
    {
      id: 'copy-ea-emails',
      text: `Copy EA Email Addresses (${eaCount})`,
      iconName: 'copy' as const,
      disabled: eaCount === 0
    }
  ];

  const handleItemClick = ({ detail }: { detail: { id: string } }) => {
    switch (detail.id) {
      case 'view-badger':
        onMultiBadgerClick?.();
        break;
      case 'download-csv':
        onDownloadCsv?.();
        break;
      case 'copy-attendee-all':
        handleCopyAttendees('', ALL_PRIORITIES);
        break;
      case 'copy-attendee-critical':
        handleCopyAttendees(AttendeePriority.Critical, [AttendeePriority.Critical]);
        break;
      case 'copy-attendee-required':
        handleCopyAttendees(AttendeePriority.Required, [AttendeePriority.Required]);
        break;
      case 'copy-attendee-critical-required':
        handleCopyAttendees(`${AttendeePriority.Critical} & ${AttendeePriority.Required}`, CRITICAL_REQUIRED);
        break;
      case 'copy-attendee-optional':
        handleCopyAttendees(AttendeePriority.Optional, [AttendeePriority.Optional]);
        break;
      case 'copy-ea-emails':
        handleCopyExecutiveAssistantsForOutlook();
        break;
    }
  };

  return (
    <>
      <div ref={buttonRef} className="copy-dropdown-wrapper">
        <ButtonDropdown
          items={dropdownItems}
          variant="normal"
          disabled={resolvedAttendees.length === 0}
          onItemClick={handleItemClick}
          ariaLabel="Attendee Actions options"
        >
          Attendee Actions
        </ButtonDropdown>

        <div aria-live="polite" aria-atomic="true" className="copy-dropdown-aria-live">
          {announceMessage}
        </div>
      </div>

      {flashMessages.length > 0 &&
        createPortal(
          <div
            className="copy-dropdown-flashbar-container"
            style={{
              top: `${flashPosition.top}px`,
              left: `${flashPosition.left}px`
            }}
          >
            <Flashbar
              items={flashMessages.map((msg) => ({
                type: msg.type,
                content: msg.content,
                dismissible: true,
                onDismiss: () => removeFlashMessage(msg.id),
                id: msg.id
              }))}
            />
          </div>,
          document.body
        )}
    </>
  );
};
